import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Checkbox,
  TextField,
  Tooltip,
} from "@mui/material";
import theme from "../utils/theme";
import IconButtons from "./IconButtons";
import ImagePreviewModal from "./ImagePreviewModal";

// Consistent styles with DimensionTable.js
const styles = {
  tableContainer: {
    borderRadius: 2,
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.05)",
    border: `${theme.palette.grey[300]} 1.5px solid`,
  },
  headerBox: {
    display: "flex",
    alignItems: "center",
    p: 1,
    bgcolor: theme.palette.background.dark,
    minHeight: 56,
  },
  title: {
    fontWeight: 600,
    color: theme.palette.text.light,
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  actionButtonsContainer: {
    display: "flex",
    gap: 1,
  },
  viewButton: {
    color: theme.palette.secondary.main,
    background: theme.palette.background.paper,
    border: `${theme.palette.grey[300]} 1.5px solid`,
    borderRadius: 2,
    boxShadow: 1,
    width: 36,
    height: 36,
    mr: 1,
    transition: "background 0.2s, color 0.2s, border-color 0.2s",
    "&:hover": {
      background: theme.palette.background.paper,
      color: theme.palette.secondary.main,
      borderColor: theme.palette.secondary.main,
    },
  },
  editButton: {
    color: theme.palette.info.main,
    background: theme.palette.background.paper,
    border: `${theme.palette.grey[300]} 1.5px solid`,
    borderRadius: 2,
    width: 32,
    height: 32,
    boxShadow: 1,
    transition: "background 0.2s, color 0.2s",
    "&:hover": {
      background: theme.palette.background.paper,
      color: theme.palette.info.dark,
      borderColor: theme.palette.info.dark,
    },
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: "1rem",
    borderBottom: "2px solid #e0e0e0",
    bgcolor: "#f5f7fa",
    color: "#222",
    py: 1.5,
  },
  tableRow: {
    bgcolor: "#fafbfc",
    transition: "background 0.2s",
    "&:hover": {
      bgcolor: "#e9f4ff",
    },
    "&:last-child td, &:last-child th": { border: 0 },
  },
  oddTableRow: {
    bgcolor: "#f3f4f6",
  },
  subpartCell: {
    flex: 1,
    borderBottom: "1px solid #f0f0f0",
    p: 1.5,
    height: "42px",
    display: "flex",
    alignItems: "center",
  },
  lastSubpartCell: {
    borderBottom: "none",
  },
  textField: {
    width: 140,
    bgcolor: theme.palette.background.paper,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.grey[500],
      },
      "&.Mui-disabled": {
        "& fieldset": {
          borderColor: theme.palette.grey[500],
        },
      },
    },
    "& .MuiInputBase-input": {
      padding: "8px 10px",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.grey[500],
    },
  },
};

// Base columns always shown
const BASE_COLUMNS = [
  { id: "no", label: "No", width: "8%" },
  { id: "part_area", label: "Part / Area", width: "20%" },
  { id: "part_location", label: "Part / Location", width: "20%" },
  { id: "inspection", label: "Item / Part Inspection", width: "24%" },
];

// Additional columns for stages mode
const STAGE_COLUMNS = [
  { id: "visual_check", label: "Visual Check", width: "14%" },
  { id: "remark", label: "Remark/Note", width: "14%" },
];

/**
 * Helper component for stage fields
 */
const StageFieldsCell = ({
  rowIndex,
  subpartIndex,
  field,
  value,
  onChange,
  disabled = false,
}) => {
  if (field === "visual_check") {
    return (
      <Checkbox
        checked={!!value}
        disabled={disabled}
        onChange={(e) =>
          onChange(rowIndex, subpartIndex, field, e.target.checked)
        }
        sx={{
          "&:hover": {
            bgcolor: "rgba(0, 102, 204, 0.04)",
            borderRadius: "4px",
          },
        }}
      />
    );
  } else {
    return (
      <TextField
        size="small"
        variant="outlined"
        value={value || ""}
        disabled={disabled}
        onChange={(e) =>
          onChange(rowIndex, subpartIndex, field, e.target.value)
        }
        fullWidth
      />
    );
  }
};

/**
 * partTable - Table for painting checklist parts
 * @param {Object[]} data - Array of part objects { no, part_area, part_location, inspection }
 * @param {boolean} [striped=true] - Whether to use striped rows
 * @param {boolean} [showStageFields=false] - Whether to show Visual Check and Remark/Note columns
 * @param {function} [onPartTableRowsChange] - Callback when table data changes
 * @param {Object[]} [partTableRows] - Array of part objects with field values, used instead of local state
 * @param {string} [title='Parts Table'] - Title to display in the header
 * @param {Array} [images=[]] - Array of image objects with image_url property
 * @param {function} [onEdit] - Callback when edit button is clicked
 * @param {boolean} [viewOnly=false] - Whether the table is view only (no edit buttons)
 */
const PartTable = ({
  data = [],
  striped = true,
  showStageFields = true,
  onPartTableRowsChange = () => {},
  partTableRows = null,
  title = "Parts Table",
  images = [],
  onEdit,
  viewOnly = false,
  viewonly, // also accept lowercased variant from parent
  // Formik validation props for rendering field errors
  errors,
  touched,
}) => {
  const isViewOnly = viewOnly || Boolean(viewonly);
  // Determine which columns to show
  const COLUMNS = [...BASE_COLUMNS, ...(showStageFields ? STAGE_COLUMNS : [])];

  // Get rows to display - use partTableRows if provided, otherwise use data
  const displayRows = partTableRows || data;

  // Handle stage field changes
  const handleStageFieldChange = (rowIndex, subpartIndex, field, value) => {
    // Create a deep copy of the rows
    const updatedRows = JSON.parse(JSON.stringify(displayRows));

    // Ensure the inspection field is an array with the proper structure
    if (!Array.isArray(updatedRows[rowIndex].painting_part_items)) {
      updatedRows[rowIndex].painting_part_items = [];
    }

    // Ensure the inspection object exists for this subpart
    if (!updatedRows[rowIndex].painting_part_items[subpartIndex]) {
      updatedRows[rowIndex].painting_part_items[subpartIndex] = {
        description: "",
      };
    }

    // Update the field value
    updatedRows[rowIndex].painting_part_items[subpartIndex][field] = value;

    // Notify parent component to update its state
    onPartTableRowsChange(updatedRows);
  };

  // State for image preview modal
  const [carouselOpen, setCarouselOpen] = useState(false);
  // Get base URL for images from environment variables
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "";

  // Process images to handle both existing and newly added images
  const processedImages = images?.map((image) => {
    // Case 1: File object (newly uploaded)
    if (image instanceof File) {
      return URL.createObjectURL(image);
    }
    
    // Case 2: Image with preview property (newly uploaded with preview)
    if (image.preview) {
      return image.preview;
    }
    
    // Case 3: Existing image with image_url
    if (image.image_url) {
      // If image_url already starts with http/https, use it as is
      if (image.image_url.startsWith('http')) {
        return image.image_url;
      }
      // Otherwise, prepend the base URL
      return `${baseUrl}${image.image_url}`;
    }
    
    // Fallback: return as is
    return image;
  }) || [];

  return (
    <>
      {/* Image Preview Modal */}
      {processedImages.length > 0 && (
        <ImagePreviewModal
          images={processedImages}
          open={carouselOpen}
          onClose={() => setCarouselOpen(false)}
        />
      )}

      <TableContainer component={Paper} sx={styles.tableContainer}>
        {/* Header with title and action buttons */}
        <Box sx={styles.headerBox}>
          <Typography variant="subtitle1" sx={styles.title}>
            {title}
          </Typography>

          {/* View Images button */}
          {images && images.length > 0 && (
            <Tooltip title={`View Images`} arrow placement="top">
              <IconButtons
                onClick={() => setCarouselOpen(true)}
                buttonType="view"
                aria-label="View Images"
                sx={{ mr: 1 }}
              />
            </Tooltip>
          )}

          {/* Edit button - shown when edit function is provided */}
          {onEdit && (
            <Box sx={styles.actionButtonsContainer}>
              <Tooltip title="Edit Template" arrow placement="top">
                <IconButtons
                  onClick={onEdit}
                  buttonType="edit"
                  aria-label="Edit template"
                />
              </Tooltip>
            </Box>
          )}
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    sx={{ ...styles.tableHeadCell, width: col.width }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No parts added yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayRows.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      ...styles.tableRow,
                      ...(striped && idx % 2 !== 0 && styles.oddTableRow),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {row.no ?? idx + 1}
                    </TableCell>
                    <TableCell>{row.part_name}</TableCell>
                    <TableCell>{row.part_location}</TableCell>
                    <TableCell>
                      {Array.isArray(row.painting_part_items) ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          height="100%"
                        >
                          {row.painting_part_items.map((inspection, i) => {
                            const desc =
                              typeof inspection === "string"
                                ? inspection
                                : inspection.item_name;
                            return (
                              <Box
                                key={i}
                                sx={{
                                  ...styles.subpartCell,
                                  ...(i ===
                                    row.painting_part_items.length - 1 &&
                                    styles.lastSubpartCell),
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    height: "100%",
                                  }}
                                >
                                  {desc?.trim() !== "" ? desc : "\u00A0"}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          {row.painting_part_items}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Conditional Stage Fields */}
                    {showStageFields && (
                      <>
                        {/* Visual Check Column */}
                        <TableCell>
                          {Array.isArray(row.painting_part_items) ? (
                            <Box
                              display="flex"
                              flexDirection="column"
                              height="100%"
                            >
                              {row.painting_part_items.map((_, i) => {
                                const fieldError =
                                  errors?.[idx]?.painting_part_items?.[i]
                                    ?.visual_check;
                                const fieldTouched =
                                  touched?.[idx]?.painting_part_items?.[i]
                                    ?.visual_check;
                                return (
                                  <Box
                                    key={i}
                                    sx={{
                                      ...styles.subpartCell,
                                      ...(i ===
                                        row.painting_part_items.length - 1 &&
                                        styles.lastSubpartCell),
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <StageFieldsCell
                                      rowIndex={idx}
                                      subpartIndex={i}
                                      field="visual_check"
                                      value={
                                        row.painting_part_items?.[i]
                                          ?.visual_check || false
                                      }
                                      onChange={handleStageFieldChange}
                                      disabled={isViewOnly}
                                    />
                                    {fieldError && fieldTouched && (
                                      <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ mt: 0.5 }}
                                      >
                                        {fieldError}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </TableCell>

                        {/* Remark/Note Column */}
                        <TableCell>
                          {Array.isArray(row.painting_part_items) ? (
                            <Box
                              display="flex"
                              flexDirection="column"
                              height="100%"
                            >
                              {row.painting_part_items.map((_, i) => {
                                const fieldError =
                                  errors?.[idx]?.painting_part_items?.[i]
                                    ?.remark;
                                const fieldTouched =
                                  touched?.[idx]?.painting_part_items?.[i]
                                    ?.remark;
                                return (
                                  <Box
                                    key={i}
                                    sx={{
                                      ...styles.subpartCell,
                                      ...(i ===
                                        row.painting_part_items.length - 1 &&
                                        styles.lastSubpartCell),
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      variant="outlined"
                                      fullWidth
                                      sx={{ minWidth: "300px" }}
                                      value={
                                        row.painting_part_items?.[i]?.remark ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        handleStageFieldChange(
                                          idx,
                                          i,
                                          "remark",
                                          e.target.value
                                        )
                                      }
                                      disabled={isViewOnly}
                                      error={Boolean(
                                        fieldError && fieldTouched
                                      )}
                                      helperText={
                                        fieldTouched && fieldError
                                          ? fieldError
                                          : ""
                                      }
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
    </>
  );
};

export default PartTable;
